import React, { useEffect, useState, useCallback } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import JuryBoxAppBar from '../../components/JuryBoxAppBar';
import OfflineBar from '../../components/OfflineBar';
import MaterialTableModule from 'material-table';
const MaterialTable = MaterialTableModule.default || MaterialTableModule;
import { tableIcons } from '../../util/icons';
import { useAlert } from '../../util/alertDialogContext';
import LinearProgress from '@mui/material/LinearProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../util/authContext';
import { listTemplates, deleteTemplate } from '../../db/sync/templateApi';
import EditModal from './EditModal';
import ShareModal from './ShareModal';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: theme.spacing(2),
  },
  table: {
    '& tbody>.MuiTableRow-root:hover': {
      background: 'rgba(0, 0, 0, 0.07)',
    },
  },
}));

const columns = [
  { title: 'Name', field: 'name' },
  { title: 'Owner', field: 'creator' },
  {
    title: 'Access',
    field: 'accessLabel',
    sorting: false,
    render: (rowData) => (
      <Stack direction="row" spacing={1}>
        {rowData.isOwner ? (
          <Chip label="Owner" size="small" color="primary" variant="outlined" />
        ) : (
          <Chip label={rowData.canEdit ? 'Shared: can edit' : 'Shared: view only'} size="small" color="info" variant="outlined" />
        )}
      </Stack>
    ),
  },
];

const Templates = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { data: authUser } = useAuth();
  const sessionEmail = authUser?.email;
  const { alert } = useAlert();

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [editModal, setEditModal] = useState({ open: false, template: null });
  const [shareModal, setShareModal] = useState({ open: false, templateId: null, templateName: null });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTemplates();
      const list = Array.isArray(data) ? data : (data?.templates || []);
      setTemplates(list);
    } catch (err) {
      console.error('Failed to list templates:', err);
      setError(err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleDelete = (rowData) => {
    alert({
      msg: `Are you sure you want to delete the template "${rowData.name}"? This can't be undone.`,
      onSubmit: async () => {
        try {
          await deleteTemplate(rowData._id);
          await refresh();
        } catch (err) {
          console.error('Failed to delete template:', err);
          setError(err);
        }
      },
      variant: 'danger',
    });
  };

  // The server distinguishes owner/view/edit access; tolerate a couple of
  // reasonable response shapes (an explicit `accessLevel` string, or a
  // `canEdit` boolean) since the API contract was finalized concurrently.
  // Unlike legacy cases (which predate the creator field and default missing
  // creator to "mine"), every template has always had a server-set creator —
  // there's no legacy-data reason to treat a missing one as owned, and doing
  // so would be the wrong-direction default for showing edit/delete/share UI.
  const rows = templates.map((t) => {
    const isOwner = !!t.creator && t.creator === sessionEmail;
    const canEdit = isOwner || t.canEdit === true || t.accessLevel === 'edit';
    return {
      ...t,
      isOwner,
      canEdit,
      canShare: isOwner,
      canDelete: isOwner,
    };
  });

  return (
    <div id="app-wrapper">
      <OfflineBar />
      <JuryBoxAppBar title="Templates" />
      {loading && <LinearProgress />}
      {!!error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error.toString().replace('Error: ', '')}
        </Alert>
      )}
      <div className={classes.root}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Tooltip title="Back to Cases">
            <IconButton onClick={() => navigate('/')} size="small">
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Typography variant="h5">
            <b>Templates</b>
          </Typography>
        </Stack>
        <div className={classes.table}>
          <MaterialTable
            columns={columns}
            data={rows}
            options={{
              showTitle: false,
              searchFieldAlignment: 'left',
              search: true,
              paging: false,
              sorting: true,
              headerStyle: { fontWeight: 'bold', fontSize: 16 },
              actionsColumnIndex: -1,
            }}
            icons={tableIcons}
            actions={[
              {
                icon: tableIcons.Add,
                tooltip: 'Create Template',
                isFreeAction: true,
                onClick: () => setEditModal({ open: true, template: null }),
              },
              {
                icon: tableIcons.Refresh,
                tooltip: 'Refresh list',
                isFreeAction: true,
                onClick: () => refresh(),
              },
              (rowData) => ({
                icon: tableIcons.Edit,
                tooltip: rowData.canEdit ? 'Edit Template' : "You only have view access to this template",
                disabled: !rowData.canEdit,
                onClick: (event) => {
                  event.preventDefault();
                  setEditModal({ open: true, template: rowData });
                },
              }),
              (rowData) => ({
                icon: tableIcons.Share,
                tooltip: rowData.canShare ? 'Share Template' : "Only the owner can share this template",
                disabled: !rowData.canShare,
                onClick: (event) => {
                  event.preventDefault();
                  setShareModal({ open: true, templateId: rowData._id, templateName: rowData.name });
                },
              }),
              (rowData) => ({
                icon: tableIcons.Delete,
                tooltip: rowData.canDelete ? 'Delete Template' : "Only the owner can delete this template",
                disabled: !rowData.canDelete,
                onClick: (event) => {
                  event.preventDefault();
                  handleDelete(rowData);
                },
              }),
            ]}
          />
        </div>
      </div>
      <EditModal
        open={editModal.open}
        template={editModal.template}
        onClose={() => setEditModal({ open: false, template: null })}
        onSaved={() => refresh()}
      />
      <ShareModal
        open={shareModal.open}
        templateId={shareModal.templateId}
        templateName={shareModal.templateName}
        onClose={() => setShareModal({ open: false, templateId: null, templateName: null })}
      />
    </div>
  );
};

export default Templates;
